import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { RippleModule } from 'primeng/ripple';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MapMonitoringObject } from '@6grain/masofa-monolith-api-client/map';
import { MapMonitoringObjectService } from '@6grain/masofa-monolith-api-client/map';
import { BehaviorSubject, Subscription } from 'rxjs';
import { OpenStreetMapComponent } from '@components/openstreetmap/openstreetmap.component';
import { ModalFormDelete } from '@components/modal-form-delete/modal-form-delete';

@Component({
  selector: 'app-monitoring-points-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonModule, TooltipModule, DialogModule, RippleModule],
  providers: [DialogService],
  templateUrl: './monitoring-points-dialog.component.html',
  styleUrls: ['./monitoring-points-dialog.component.scss']
})
export class MonitoringPointsDialogComponent implements OnChanges, OnInit, OnDestroy {
  @Input() visible: boolean = true;
  @Input() monitoringObjects$!: BehaviorSubject<MapMonitoringObject[]>;
  @Input() openStreetMapComponent!: OpenStreetMapComponent;
  @Input() isLoading: boolean = false;
  @Input() selectedPointId: number | null = null;
  @Input() selectedPolygonId: string | null = null;
  @Input() viewMode: 'points' | 'polygons' = 'points';
  @Input() popupPosition: { top: number; right: number } = { top: 100, right: 20 };

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() viewModeChange = new EventEmitter<'points' | 'polygons'>();
  @Output() editPoint = new EventEmitter<any>();
  @Output() deletePoint = new EventEmitter<any>();
  @Output() editPolygon = new EventEmitter<any>();
  @Output() deletePolygon = new EventEmitter<any>();
  @Output() openAllPointsChart = new EventEmitter<void>();
  @Output() switchToPolygons = new EventEmitter<void>();
  @Output() openWeatherModal = new EventEmitter<{ lat: number, lng: number, data: any }>();

  searchText: string = '';
  filteredPoints: MapMonitoringObject[] = [];
  filteredPolygons: MapMonitoringObject[] = [];
  editingPointId: string | null = null; // Track which point is being edited
  deletingPointId: string | null = null; // Track which point is being deleted

  private points: MapMonitoringObject[] = [];
  private polygons: MapMonitoringObject[] = [];
  private subscription?: Subscription;
  private ref?: DynamicDialogRef;

  constructor(
    private dialogService: DialogService,
    private translate: TranslateService,
    private mapMonitoringObjectService: MapMonitoringObjectService
  ) {}

  ngOnInit(): void {
    if (this.monitoringObjects$) {
      this.subscription = this.monitoringObjects$.subscribe(objects => {
        this.separatePointsAndPolygons(objects);
        this.filterItems();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['monitoringObjects$']) {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      if (this.monitoringObjects$) {
        this.subscription = this.monitoringObjects$.subscribe(objects => {
          this.separatePointsAndPolygons(objects);
          this.filterItems();
        });
      }
    }
  }

  /**
   * Separate monitoring objects into points and polygons
   */
  private separatePointsAndPolygons(objects: MapMonitoringObject[]): void {
    this.points = objects.filter(obj =>
      obj.pointJson && obj.polygonJson?.length ===0,
    );
    this.polygons = objects.filter(obj =>
      obj.polygonJson && obj.polygonJson.length > 0
    );
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onViewModeChange(mode: 'points' | 'polygons'): void {
    this.viewMode = mode;
    this.viewModeChange.emit(mode);

    if (mode === 'polygons') {
      this.switchToPolygons.emit();
    }
  }

  onSearchChange(text: string): void {
    this.searchText = text;
    this.filterItems();
  }

  filterItems(): void {
    // Filter points
    if (!this.searchText) {
      this.filteredPoints = this.points;
    } else {
      this.filteredPoints = this.points.filter(point => {
        const name = point.name || '';
        return name.toLowerCase().includes(this.searchText.toLowerCase());
      });
    }

    // Filter polygons
    if (!this.searchText) {
      this.filteredPolygons = this.polygons;
    } else {
      this.filteredPolygons = this.polygons.filter(polygon => {
        const name = polygon.name || '';
        return name.toLowerCase().includes(this.searchText.toLowerCase());
      });
    }
  }

  onGoToLocation(point: MapMonitoringObject): void {
    this.onZoomToPoint(point);
  }

  onZoomToPoint(point: MapMonitoringObject): void {
    if (!this.openStreetMapComponent || !point.pointJson) {
      return;
    }

    try {
      const pointData = point.pointJson;

      const lat = pointData.lat;
      const lng = pointData.lon;

      if (lat && lng) {
        this.openStreetMapComponent.mapController$.next({
          setCenter: { lat, lng, zoom: 15 }
        });
      }
    } catch (error) {
      console.error('Error zooming to point:', error);
    }
  }

  onGoToPolygon(polygon: MapMonitoringObject): void {
    this.onZoomToPolygon(polygon);
  }

  onZoomToPolygon(polygon: MapMonitoringObject): void {
    if (!this.openStreetMapComponent || !polygon.polygonJson || polygon.polygonJson.length === 0) {
      return;
    }

    try {
      // Calculate center of polygon
      let totalLat = 0;
      let totalLng = 0;
      let count = 0;

      polygon.polygonJson.forEach((coord: any) => {
        const lat = coord[1] || coord.lat || coord.latitude;
        const lng = coord[0] || coord.lng || coord.longitude;

        if (lat && lng) {
          totalLat += lat;
          totalLng += lng;
          count++;
        }
      });

      if (count > 0) {
        const centerLat = totalLat / count;
        const centerLng = totalLng / count;

        this.openStreetMapComponent.mapController$.next({
          setCenter: { lat: centerLat, lng: centerLng, zoom: 14 }
        });
      }
    } catch (error) {
      console.error('Error zooming to polygon:', error);
    }
  }

  onEditPoint(point: any): void {
    this.editPoint.emit(point);
  }

  onDeletePoint(point: MapMonitoringObject): void {
    this.ref = this.dialogService.open(ModalFormDelete, {
      header: this.translate.instant('Delete Confirmation'),
      width: '30rem',
      closable: true,
      modal: true,
      data: {
        message: this.translate.instant('Are you sure you want to delete this item ?') + ` "${point.name || 'this monitoring point'}"?`
      }
    });

    this.ref.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        // Set deleting state
        this.deletingPointId = point.id as string;

        // Delete the monitoring point directly
        if (point.id) {
          this.mapMonitoringObjectService.tilesMapMonitoringObjectDeleteIdDelete(point.id).subscribe({
            next: () => {
              console.log('Monitoring point deleted successfully');
              // Refresh the list by removing the deleted item
              const currentObjects = this.monitoringObjects$.value;
              const updatedObjects = currentObjects.filter(obj => obj.id !== point.id);
              this.monitoringObjects$.next(updatedObjects);

              // Also emit to parent if needed
              this.deletePoint.emit(point);

              // Clear deleting state
              this.deletingPointId = null;
            },
            error: (error) => {
              console.error('Error deleting monitoring point:', error);
              // Clear deleting state on error
              this.deletingPointId = null;
            }
          });
        }
      }
    });
  }

  onEditPolygon(polygon: any): void {
    this.editPolygon.emit(polygon);
  }

  onDeletePolygon(polygon: MapMonitoringObject): void {
    this.ref = this.dialogService.open(ModalFormDelete, {
      header: this.translate.instant('Delete Confirmation') ,
      width: '30rem',
      closable: true,
      modal: true,
      data: {
        message: this.translate.instant('Are you sure you want to delete this item ?') + ` "${polygon.name}"?`
      }
    });

    this.ref.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        // Set deleting state
        this.deletingPointId = polygon.id as string;

        // Delete the polygon directly
        if (polygon.id) {
          this.mapMonitoringObjectService.tilesMapMonitoringObjectDeleteIdDelete(polygon.id).subscribe({
            next: () => {
              // Refresh the list by removing the deleted item
              const currentObjects = this.monitoringObjects$.value;
              const updatedObjects = currentObjects.filter(obj => obj.id !== polygon.id);
              this.monitoringObjects$.next(updatedObjects);

              // Also emit to parent if needed
              this.deletePolygon.emit(polygon);

              // Clear deleting state
              this.deletingPointId = null;
            },
            error: (error) => {
              console.error('Error deleting polygon:', error);
              // Clear deleting state on error
              this.deletingPointId = null;
            }
          });
        }
      }
    });
  }

  /**
   * Open weather modal for a monitoring point
   */
  onOpenWeatherForPoint(point: MapMonitoringObject): void {
    if (point.pointJson && point.pointJson.lat && point.pointJson.lon) {
      this.openWeatherModal.emit({
        lat: point.pointJson.lat,
        lng: point.pointJson.lon,
        data: {
          id: point.id,
          name: point.name,
          type: point.type,
          ... point,
        }
      });
    }
  }

  /**
   * Open weather modal for a polygon (using center point)
   */
  onOpenWeatherForPolygon(polygon: MapMonitoringObject): void {
    if (polygon.polygonJson && polygon.polygonJson.length > 0) {
      // Calculate center of polygon
      let totalLat = 0;
      let totalLng = 0;
      let count = 0;

      polygon.polygonJson.forEach((coord: any) => {
        const lat = coord.lat || 0;
        const lng = coord.lon || 0;

        if (lat && lng) {
          totalLat += lat;
          totalLng += lng;
          count++;
        }
      });

      if (count > 0) {
        const centerLat = totalLat / count;
        const centerLng = totalLng / count;

        this.openWeatherModal.emit({
          lat: centerLat,
          lng: centerLng,
          data: {
            monitoringObjectId: polygon.id,
            id:polygon.id,
            name: polygon.name,
            type: polygon.type,
            polygonJson: polygon.polygonJson,
          }
        });
      }
    }
  }

  onOpenAllPointsChart(): void {
    this.openAllPointsChart.emit();
  }
}
